import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/topics";
import { categoryNameMap } from "@/mocks/topics";

interface HeroProps {
  feature: Topic;
  latest: Topic[];
}

export default function Hero({ feature, latest }: HeroProps) {
  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-background-100">
      <div className="paper-grain absolute inset-0" />
      <div className="relative mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="animate-fade-up lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium tracking-wider text-primary-700">
              <i className="ri-bookmark-3-line" />
              第 128 期 · 重点档案
            </span>
            <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
              看料档案
              <span className="mt-3 block text-primary-600">
                深挖事件，把背后的逻辑摊开给你看
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              我们不做最快的那一个，只做最完整的那一个。围绕商业起底、行业内幕与舆情深挖三个方向，把散落的信息重新拼回时间线与利益链，让每一次公共事件都留下可复查的档案。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/topics"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-center text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                进入深挖专区
              </Link>
              <Link
                to="/about"
                className="whitespace-nowrap rounded-md border border-foreground-900 px-6 py-3 text-center text-sm font-medium text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
              >
                了解我们的内容原则
              </Link>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-foreground-500">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <i className="ri-database-2-line text-primary-500" />
                1286 篇归档专题
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <i className="ri-time-line text-primary-500" />
                平均阅读 9.4 分钟
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <i className="ri-shield-check-line text-primary-500" />
                信息出处分级标注
              </span>
            </div>
          </div>

          <div className="animate-fade-up delay-2 lg:col-span-5">
            <article className="overflow-hidden rounded-xl2 bg-foreground-950">
              <div className="relative h-52 w-full md:h-60">
                <img
                  src={feature.cover}
                  alt={feature.title}
                  title={`${feature.title} - 看料档案重点专题`}
                  className="h-full w-full object-cover object-top opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent" />
                <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-foreground-950">
                  本期头版
                </span>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-3 text-xs text-background-400">
                  <span className="whitespace-nowrap">{categoryNameMap[feature.categoryId]}</span>
                  <span className="whitespace-nowrap">{feature.date}</span>
                </div>
                <h2 className="mt-3 font-heading text-xl font-semibold leading-snug text-background-50">
                  <Link to={`/topics/${feature.slug}`} className="transition-colors hover:text-accent-400">
                    {feature.title}
                  </Link>
                </h2>
                <p className="mt-3 line-clamp-2 text-sm leading-6 text-background-300">
                  {feature.excerpt}
                </p>
                <Link
                  to={`/topics/${feature.slug}`}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-accent-400"
                >
                  阅读完整档案
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </article>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-background-300 pt-6 md:flex-row md:items-center md:gap-6">
          <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium tracking-wider text-foreground-950">
            <span className="flex h-2 w-2 rounded-full bg-primary-500" />
            最新归档
          </span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {latest.map((topic) => (
              <Link
                key={topic.id}
                to={`/topics/${topic.slug}`}
                className="whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
              >
                {topic.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}